import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { Modal } from '../common/Modal';
import { Tag } from '../common/Tag';
import { fmtDateTime } from '../../utils/helpers';
import DocumentPreviewModal from './DocumentPreviewModal';
import { documentTypeServices } from '../../APIServices/documentTypeServices';
import { documentTagServices } from '../../APIServices/documentTagServices';
import { studyFolderAndDocumentServices } from '../../APIServices/studyFolderAndDocumentServices';

function DocumentDetailPanel({ document, onClose, onUpdated }) {
  const { state, audit } = useData();
  const toast = useToast();
  const [documentTypeList, setDocumentTypeList] = useState([]);
  const [documentTagList, setDocumentTagList] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(document);
  const [showVersion, setShowVersion] = useState(false);
  const [versionForm, setVersionForm] = useState({ version: '', reason: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const getSelectedValues = (event) => Array.from(event.target.selectedOptions || []).map((option) => option.value).filter(Boolean);

  useEffect(() => {
    setForm(document);
    setEditing(false);
  }, [document]);

  useEffect(() => {
    const getMetadata = async () => {
      try {
        const [documentTypeData, documentTagData] = await Promise.all([
          documentTypeServices.getDocumentTypeData('active', ''),
          documentTagServices.getDocumentTagData('active', ''),
        ]);

        setDocumentTypeList(documentTypeData?.data || []);
        setDocumentTagList(documentTagData?.data || []);
      } catch (error) {
        setDocumentTypeList([]);
        setDocumentTagList([]);
      }
    };

    getMetadata();
  }, []);

  if (!document) return null;

  const save = async () => {
    try {
      const data = await studyFolderAndDocumentServices.updateStudyFolderAndDocument({
        uniqueId: document.uniqueId,
        name: form.name,
        documentType: form.type,
        documentTag: Array.isArray(form.tags) ? form.tags : [form.tags].filter(Boolean),
        status: form.status,
        confidentiality: form.confidentiality,
        version: form.version,
      });

      if (data?.statusCode === 200) {
        audit('DOCUMENT_METADATA_EDIT', document.name, 'Updated metadata', { studyId: document.studyId, documentId: document.id });
        toast('Metadata updated', 'ok');
        setEditing(false);
        onUpdated?.();
      }
    } catch (error) {
      toast('Unable to update metadata.', 'warn');
    }
  };

  const submitVersion = async () => {
    if (!versionForm.version || !versionForm.reason) {
      toast('Version number and reason required', 'warn');
      return;
    }

    try {
      const data = await studyFolderAndDocumentServices.updateStudyFolderAndDocument({
        uniqueId: document.uniqueId,
        version: versionForm.version,
        status: 'review',
        description: versionForm.reason,
      });

      if (data?.statusCode === 200) {
        audit('DOCUMENT_NEW_VERSION', document.name, `v${document.version} to v${versionForm.version}: ${versionForm.reason}`, { studyId: document.studyId, documentId: document.id });
        toast(`New version v${versionForm.version} created`, 'ok');
        setShowVersion(false);
        setVersionForm({ version: '', reason: '' });
        onUpdated?.();
      }
    } catch (error) {
      toast('Unable to create new version.', 'warn');
    }
  };

  const submitDelete = async () => {
    if (!deleteReason) {
      toast('Reason required for soft delete', 'warn');
      return;
    }

    try {
      const data = await studyFolderAndDocumentServices.softDeleteRestoreStudyFolderAndDocument({
        id: document._id,
        uniqueId: document.uniqueId,
        type: 'delete',
      });

      if (data?.statusCode === 200) {
        audit('DOCUMENT_SOFT_DELETE', document.name, deleteReason, { studyId: document.studyId, documentId: document.id });
        toast(`${document.name} soft-deleted (restorable)`, 'warn');
        setConfirmDelete(false);
        setDeleteReason('');
        onUpdated?.();
        onClose();
      }
    } catch (error) {
      toast('Unable to delete document.', 'warn');
    }
  };

  const approve = async () => {
    try {
      const data = await studyFolderAndDocumentServices.updateStudyFolderAndDocument({
        uniqueId: document.uniqueId,
        status: 'approved',
      });

      if (data?.statusCode === 200) {
        audit('DOCUMENT_APPROVE', document.name, 'Status: review to approved', { studyId: document.studyId, documentId: document.id });
        toast(`${document.name} approved`, 'ok');
        onUpdated?.();
      }
    } catch (error) {
      toast('Unable to approve document.', 'warn');
    }
  };

  const owner = state.users.find((u) => String(u.id) === String(document.updatedBy || document.owner));
  const statusVariant = (s) => ({ approved: 'ok', review: 'warn', active: 'info', draft: 'default', superseded: 'default', archived: 'default' })[s] || 'default';

  return (
    <div>
      <div className="flex between center mb-3">
        <div className="flex gap-2 center">
          <i className="ti ti-file-text" style={{ fontSize: 22, color: 'var(--ink-3)' }} />
          <div>
            <div className="bold sm">{document.name}</div>
            <div className="xs muted">{document.type} · v{document.version} · {document.size}</div>
          </div>
        </div>
        <div className="flex gap-1">
          {!editing && (
            <button className="icon-btn" onClick={() => setEditing(true)} title="Edit metadata">
              <i className="ti ti-edit" />
            </button>
          )}
          <button className="icon-btn" onClick={onClose} title="Close">
            <i className="ti ti-x" />
          </button>
        </div>
      </div>

      <div className="card-title mt-3">Metadata</div>

      {editing ? (
        <>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="field-label">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="">Select document type</option>
                {documentTypeList.length === 0 ? (
                  <option value="">No document types found</option>
                ) : (
                  documentTypeList.map((item) => (
                    <option key={item._id} value={item.documentType}>{item.documentType}</option>
                  ))
                )}
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="review">Under review</option>
                <option value="approved">Approved</option>
                <option value="superseded">Superseded</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">Confidentiality</label>
              <select value={form.confidentiality || 'Confidential'} onChange={(e) => setForm({ ...form, confidentiality: e.target.value })}>
                <option>Confidential</option>
                <option>Internal</option>
                <option>Public</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Document tag</label>
            <select
              multiple
              value={Array.isArray(form.tags) ? form.tags : [form.tags].filter(Boolean)}
              onChange={(e) => setForm({ ...form, tags: getSelectedValues(e) })}
              style={{ minHeight: 120 }}
            >
              {documentTagList.map((item) => (
                <option key={item._id} value={item.documentTag}>{item.documentTag}</option>
              ))}
            </select>
            <div className="field-hint">No tag is selected by default. Hold Ctrl or Cmd to select multiple document tags.</div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="btn btn-sm btn-primary" onClick={save}>
              <i className="ti ti-check" /> Save
            </button>
            <button className="btn btn-sm" onClick={() => { setForm(document); setEditing(false); }}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <table style={{ width: '100%', fontSize: 12 }}>
          <tbody>
            <tr><td className="muted" style={{ padding: '4px 0', width: '40%' }}>Type</td><td>{document.type}</td></tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Version</td><td>v{document.version}</td></tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Status</td>
              <td>
                {document.status === 'approved' ? <Tag variant="ok" dot>Approved</Tag> :
                 document.status === 'review' ? <Tag variant="warn" dot>Under review</Tag> :
                 document.status === 'draft' ? <Tag>Draft</Tag> :
                 <Tag>{document.status}</Tag>}
              </td>
            </tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Confidentiality</td><td>{document.confidentiality || '—'}</td></tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Document tag</td><td>{Array.isArray(document.tags) ? (document.tags.join(', ') || '—') : (document.tags || '—')}</td></tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Updated</td><td>{fmtDateTime(document.updatedAt)}</td></tr>
            <tr><td className="muted" style={{ padding: '4px 0' }}>Folder</td><td>{document.folder}</td></tr>
          </tbody>
        </table>
      )}

      <div className="divider" />

      <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
        <button className="btn btn-sm btn-primary" onClick={() => setShowPreview(true)}>
          <i className="ti ti-eye" /> Preview
        </button>
        {document.status === 'review' && (
          <button className="btn btn-sm" onClick={approve}>
            <i className="ti ti-check" /> Approve
          </button>
        )}
        <button className="btn btn-sm" onClick={() => setShowVersion(true)}>
          <i className="ti ti-versions" /> New version
        </button>
        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDelete(true)}>
          <i className="ti ti-trash" /> Soft delete
        </button>
        {document.fileUrl && (
          <a className="btn btn-sm" href={document.fileUrl} target="_blank" rel="noreferrer">
            <i className="ti ti-download" /> Download
          </a>
        )}
      </div>

      <div className="card-title mt-4">Version history</div>
      <div className="timeline">
        {(document.versions || [{ v: document.version, at: document.updatedAt, by: document.updatedBy, reason: 'Initial' }])
          .slice().reverse().map((v, i) => (
            <div key={i} className={`timeline-item ${i === 0 ? 'accent' : ''}`}>
              <div className="timeline-time">{fmtDateTime(v.at)}</div>
              <div className="timeline-title">v{v.v} {i === 0 ? '· current' : ''}</div>
              <div className="timeline-desc">{v.reason}</div>
            </div>
          ))}
      </div>

      <DocumentPreviewModal open={showPreview} onClose={() => setShowPreview(false)} document={document} />

      <Modal
        open={showVersion}
        onClose={() => setShowVersion(false)}
        title="Create new version"
        footer={
          <>
            <button className="btn" onClick={() => setShowVersion(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitVersion}>
              <i className="ti ti-versions" /> Create version
            </button>
          </>
        }
      >
        <div className="xs muted mb-3">Current version: v{document.version}. The new version supersedes the current one. History is retained for audit.</div>
        <div className="field">
          <label className="field-label">New version <span className="req">*</span></label>
          <input value={versionForm.version} onChange={(e) => setVersionForm({ ...versionForm, version: e.target.value })} placeholder="e.g. 2.1" />
        </div>
        <div className="field">
          <label className="field-label">Reason for new version <span className="req">*</span></label>
          <textarea value={versionForm.reason} onChange={(e) => setVersionForm({ ...versionForm, reason: e.target.value })} placeholder="e.g. Incorporated QA feedback on section 5" />
        </div>
      </Modal>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Soft delete document"
        footer={
          <>
            <button className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn btn-accent" onClick={submitDelete}>
              <i className="ti ti-trash" /> Soft delete
            </button>
          </>
        }
      >
        <div className="sm mb-3">
          The document <strong>{document.name}</strong> will be hidden from normal views but retained in the audit trail. An authorized user can restore it.
        </div>
        <div className="field">
          <label className="field-label">Reason for delete <span className="req">*</span></label>
          <textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="e.g. Superseded by v2.0" />
        </div>
      </Modal>
    </div>
  );
}

export default DocumentDetailPanel;